// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, in one place, so `tests/contrast.test.ts` can
// hold each of them to a ratio against the surface it is actually drawn on.
//
// This module exists because of a shipped bug elsewhere in this fleet: a wall rendered at 1.14:1
// against its board was drawn, in the right cell, at the right size, and INVISIBLE — and on a
// deliberately moody dark palette a screenshot of it reads as atmosphere. A colour literal sitting
// inline in a renderer is a colour nobody can measure, so none of them live there.
//
// Identity in brood is carried by SHAPE first: the creature is named by the glyph engraved on its
// tile, and the two sides are told apart by tile colour AND by a light or dark engraving. A player
// who cannot separate amber from sky still reads the whole board from the glyphs.

export const PALETTE = {
  /** The table the cluster sits on. */
  stage: '#15110e',
  /** Panels, the tray, the HUD. */
  panel: '#241d17',
  panelEdge: '#3a2f25',
  ink: '#f2e9dd',
  inkDim: '#a89684',

  /** The two broods. Okabe-Ito derived, so they separate under every common colour vision type,
   *  and they differ in lightness as well as hue. */
  seat0: '#e8a33d',
  seat1: '#61bdea',
  /** The engraving on a tile — dark on both, because both tile colours are light. */
  glyph: '#1b1410',
  /**
   * The heavy ring that marks a Matriarch, on both sides. DARK, not pale: a pale ring measured
   * 2.01:1 on the amber tile — a ring you cannot quite see, on the one piece whose capture ends
   * the game. Dark clears 3:1 on both broods because both tile colours are light.
   */
  crown: '#221a12',

  /** An empty cell you may place into. */
  place: '#c9b48f',
  /** An empty cell the selected piece may move to. */
  dest: '#7fe0a6',
  /** A cell a Tender may lift a tile out of, or drop one into. */
  lift: '#d79ce8',
  /** The aim marker while a crowded candidate set is waiting on a confirm. */
  aim: '#ffffff',
  /** A tile that cannot move: pinned by the cluster, held after a lift, or buried. */
  pinned: '#6f6355',
  /** A Matriarch one closed side from losing. */
  danger: '#ff7a63',
  /** A Matriarch's meter segment that is still open. */
  meterOpen: '#4a3f34',
} as const;

export type PaletteKey = keyof typeof PALETTE;

export const seatColour = (seat: number): string => (seat === 0 ? PALETTE.seat0 : PALETTE.seat1);
/** A short word for a seat, used wherever colour alone would be the only cue. */
export const seatName = (seat: number): string => (seat === 0 ? 'Amber' : 'Sky');

/**
 * WHOSE TILE IS THIS is the most-read distinction in the game, and colour alone cannot carry it.
 * The two broods are an Okabe-Ito derived pair, so they separate cleanly for the common colour
 * vision deficiencies — but they are almost identical in LUMINANCE (measured: 1.02:1), which means
 * a greyscale or achromatopsic view cannot tell them apart at all. Rather than trade away a
 * palette that works for the overwhelmingly more common cases, side carries a redundant SHAPE cue:
 * every Sky tile is drawn with a dashed inner ring, and no Amber tile has one.
 */
export const seatHasInnerRing = (seat: number): boolean => seat === 1;

// ---------------------------------------------------------------- measurement

const channel = (v: number): number => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c) : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
  const [r, g, b] = n.map((x) => parseInt(x, 16));
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two opaque colours. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Composite a colour over a background at an alpha, so a semi-transparent overlay can be measured
 *  as the colour it actually PAINTS rather than as the colour it was declared. */
export function over(fg: string, bg: string, alpha: number): string {
  const parse = (hex: string): number[] => {
    const h = hex.replace('#', '');
    const n = h.length === 3 ? h.split('').map((c) => c + c) : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
    return n.map((x) => parseInt(x, 16));
  };
  const f = parse(fg);
  const b = parse(bg);
  const mix = f.map((v, i) => Math.round(v * alpha + b[i] * (1 - alpha)));
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Simulate how a colour appears to someone with one of the three dichromacies (Viénot, Brettel &
 * Mollon 1999 — the standard linear-RGB projections). Used by the contrast test to prove the two
 * broods stay apart under each of them, rather than asserting it because the palette came from a
 * list that says it should.
 */
export type Dichromacy = 'protan' | 'deutan' | 'tritan';

const MATRICES: Record<Dichromacy, number[][]> = {
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
  tritan: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.3039],
  ],
};

const toLinear = (v: number): number => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};
const toSrgb = (v: number): number => {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(Math.max(0, v), 1 / 2.4) - 0.055;
  return Math.round(Math.min(255, Math.max(0, c * 255)));
};

function channels(hex: string): number[] {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c) : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
  return n.map((x) => parseInt(x, 16));
}

export function simulate(hex: string, kind: Dichromacy): string {
  const lin = channels(hex).map(toLinear);
  const m = MATRICES[kind];
  const out = m.map((row) => row[0] * lin[0] + row[1] * lin[1] + row[2] * lin[2]).map(toSrgb);
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Straight-line distance between two colours in sRGB, 0..441. A blunt instrument, but a
 *  sufficient one for "are these still two different colours after the simulation". */
export function colourDistance(a: string, b: string): number {
  const ca = channels(a);
  const cb = channels(b);
  return Math.hypot(ca[0] - cb[0], ca[1] - cb[1], ca[2] - cb[2]);
}

/** Emit the palette as CSS custom properties, so the stylesheet and the renderer cannot drift. */
export function paletteCss(): string {
  return Object.entries(PALETTE)
    .map(([k, v]) => `--c-${k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}: ${v};`)
    .join('');
}
